import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ISceneConfiguration, SceneConfig } from "Classes/interfaces";
import * as rooms from "assets/json/scenes.json";
import * as models from "assets/json/models.json";

export interface BsuAvailableColorType {
    name: string;
    color: string;
}

export interface availablePatientPosition {
    name: string;
    gender: string;
}

export enum SelectedBSUBSUType {
    IBsu = "I-vsu",
    VBsu = "V-vus",
}

export interface SelectedBSUType {
    name: string;
    id: string;
    selectedHeight: number;
    currentRotation: number;
    type: SelectedBSUBSUType | null;
}

export interface SelectedPortType {
    name: string;
    id: string;
    color: string;
    rotateX: number;
    rotateY: number;
}

export interface SelectedConsoleType {
    currentRotation: number;
}
export interface SelectStackType {
    currentRotation: number;
}

export interface SelectTrolleyType {
    currentRotation: number;
}

export interface SelectAnesthesiaType {
    currentRotation: number;
}

export interface SelectSurgeonType {
    currentRotation: number;
}

export interface SelectSlaveType {
    currentRotation: number;
}

// Load Patient bed colors.
const patientBeds = models.globalSettings.models.patientBed.map((bed) => {
    return {
        name: bed.name,
        gender: bed.gender,
    };
});

// Load BSU available colors.
const bsuColors = Object.keys(models.globalSettings.settings.BSUColors).map((bsu) => {
    const colors = models.globalSettings.settings.BSUColors as {
        [key: string]: string;
    };
    return {
        name: bsu,
        color: colors[bsu],
    };
});

export type SceneSliceType = {
    isSceneLoaded: boolean;
    currentScene: ISceneConfiguration;
    currentSceneKey?: string;
    sceneName?: string;
    sceneConfig: SceneConfig;
    resetScene: boolean;
    availablePatientPosition: availablePatientPosition[];
    selectedPatientPosition: string;
    bsuAvailableColors: BsuAvailableColorType[];
    selectedBSU: SelectedBSUType | null;
    selectedPort: SelectedPortType | null;
    selectConsole: SelectedConsoleType | null;
    selectedStack: SelectStackType | null;
    selectedTrolley: SelectTrolleyType | null;
    selectedAnesthesia: SelectAnesthesiaType | null;
    selectedSurgeon: SelectSurgeonType | null;
    selectedSlave: SelectSlaveType | null;
};

const sceneConf = rooms.preBuiltScenes as {
    [key: string]: ISceneConfiguration;
};

const initialState = {
    isSceneLoaded: false,
    currentScene: sceneConf[0],
    currentSceneKey: undefined,
    resetScene: false,
    availablePatientPosition: patientBeds,
    selectedPatientPosition: "",
    bsuAvailableColors: bsuColors,
    selectedBSU: null,
    selectedPort: null,
    selectedStack: null,
    selectConsole: null,
    selectedTrolley: null,
    selectedSurgeon: null,
    selectedSlave: null,
    selectedAnesthesia: null,
} as SceneSliceType;

const SceneSlice = createSlice({
    name: "scene",
    initialState: initialState,
    reducers: {
        sceneLoaded: (state, action: PayloadAction<boolean>) => {
            state.isSceneLoaded = action.payload;
        },
        setCurrentScene: (state, action: PayloadAction<ISceneConfiguration>) => {
            state.currentScene = action.payload;
        },
        setSceneConfig: (state, action: PayloadAction<SceneConfig>) => {
            state.sceneConfig = action.payload;
        },
        setCurrentSceneKey: (state, action: PayloadAction<string | undefined>) => {
            state.currentSceneKey = action.payload;
        },
        setSceneName: (state, action: PayloadAction<string>) => {
            state.sceneName = action.payload;
        },
        setResetScene: (state, action: PayloadAction<boolean>) => {
            state.resetScene = action.payload;
        },
        setSelectedPatientPosition: (state, action: PayloadAction<string>) => {
            state.selectedPatientPosition = action.payload;
        },
        setSelectedBsu: (state, action: PayloadAction<SelectedBSUType>) => {
            state.selectedBSU = action.payload;
        },
        setSelectedPort: (state, action: PayloadAction<SelectedPortType>) => {
            state.selectedPort = action.payload;
        },
        updateSelectedBsuColor: (state, action: PayloadAction<string>) => {
            state.selectedBSU = {
                name: state.selectedBSU ? state.selectedBSU.name : "",
                id: action.payload,
                currentRotation: state.selectedBSU ? state.selectedBSU.currentRotation : 0,
                selectedHeight: state.selectedBSU ? state.selectedBSU.selectedHeight : 0,
                type: state.selectedBSU ? state.selectedBSU.type : null,
            };
        },
        updateSelectedPortConnection: (state, action: PayloadAction<string>) => {
            state.selectedPort = {
                name: state.selectedPort ? state.selectedPort.name : "",
                id: action.payload,
                color: action.payload,
                rotateX: state.selectedPort?.rotateX ? state.selectedPort.rotateX : 0,
                rotateY: state.selectedPort?.rotateY ? state.selectedPort.rotateY : 0,
            };
        },
        updateSelectedBsuHeight: (state, action: PayloadAction<number>) => {
            state.selectedBSU = {
                name: state.selectedBSU ? state.selectedBSU.name : "",
                id: state.selectedBSU ? state.selectedBSU.id : "",
                currentRotation: state.selectedBSU ? state.selectedBSU.currentRotation : 0,
                selectedHeight: action.payload,
                type: state.selectedBSU ? state.selectedBSU.type : null,
            };
        },

        updateSelectedConsole: (state, action: PayloadAction<SelectedConsoleType>) => {
            state.selectConsole = action.payload;
        },
        updateSelectedStack: (state, action: PayloadAction<SelectStackType>) => {
            state.selectedStack = action.payload;
        },
        updateSelectedTrolley: (state, action: PayloadAction<SelectTrolleyType>) => {
            state.selectedTrolley = action.payload;
        },
        updateSelectedSurgeon: (state, action: PayloadAction<SelectSurgeonType>) => {
            state.selectedSurgeon = action.payload;
        },
        updateSelectedSlave: (state, action: PayloadAction<SelectSlaveType>) => {
            state.selectedSlave = action.payload;
        },
        updateSelectedAnesthesia: (state, action: PayloadAction<SelectAnesthesiaType>) => {
            state.selectedAnesthesia = action.payload;
        },
    },
});

export const {
    sceneLoaded,
    setCurrentScene,
    setCurrentSceneKey,
    setSceneName,
    setResetScene,
    setSelectedPatientPosition,
    setSelectedBsu,
    updateSelectedBsuColor,
    updateSelectedBsuHeight,
    setSelectedPort,
    updateSelectedPortConnection,
    updateSelectedConsole,
    updateSelectedStack,
    updateSelectedTrolley,
    updateSelectedSurgeon,
    updateSelectedSlave,
    updateSelectedAnesthesia,
    setSceneConfig,
} = SceneSlice.actions;

export default SceneSlice.reducer;
